// ContainerBackend — backs Workspace with a computerd instance
// running inside a Cloudflare Container.
//
// The backend drives container lifecycle through an IWorkspaceContainerAPI
// abstraction. Same-DO and cross-DO callers look identical from
// here; whether ctx.container is reached directly or through an
// RPC stub is a concern of the IWorkspaceContainerAPI implementation.
//
// Same-DO shape (one DO owns both the container and the Workspace):
//
//   class ComputerdContainer extends DurableObject<Env> {
//     #backend = new ContainerBackend({
//       container: () => this.ws,
//       workspace: { binding: "ComputerdContainer", id: this.ctx.id.toString() },
//     });
//     #workspace = new Workspace({ backends: [this.#backend] });
//
//     override fetch(req: Request): Promise<Response> {
//       return this.#backend.handleFetch(req);
//     }
//   }
//
// Cross-DO shape (Agent DO holds the Workspace, a pool member DO
// owns the container):
//
//   class AgentDO extends DurableObject<Env> {
//     #backend = new ContainerBackend({
//       container: async () => {
//         const memberId = await pickPoolMember(this.env, this.ctx.id);
//         return this.env.ComputerdHost.get(this.env.ComputerdHost.idFromString(memberId));
//       },
//       workspace: { binding: "AgentDO", id: this.ctx.id.toString() },
//     });
//   }
//
// The factory runs once per connect(), so a redial after a session
// drop re-picks the pool member; mid-session container churn is the
// pool's problem, not the backend's.
//
// Failure model: connect() does the bootstrap once and throws on
// any failure. The Workspace's ready() retries by re-entering
// connect() on the next call. On a mid-session WebSocket drop the
// backend resolves `BackendHandle.closed`, which the Workspace
// listens for and uses to drop its cached handle so the next call
// rebuilds against a fresh session.

import type { WorkspaceRPC } from "@cloudflare/computer-rpc";
import { newWebSocketRpcSession, type RpcStub } from "capnweb";

import type { BackendHandle, WorkspaceBackend } from "../../backend.js";
import { startHeartbeat } from "../../heartbeat.js";
import {
  WORKSPACE_EGRESS_TOKEN_HEADER,
  WORKSPACE_EGRESS_URL_HEADER,
  type WorkspaceEgressPolicy,
} from "../../runtime/egress.js";
import { WorkspaceTransportError } from "../../transport-failure.js";
import { WORKSPACE_ROOT } from "../../workspace-root.js";
import type { IWorkspaceContainerAPI, WorkspaceRef } from "./container-host.js";
import type { ContainerInstanceSize, ContainerLaunchSpec } from "./container-launch-record.js";
import { probeComputerdHealth } from "./health-probe.js";
import {
  assertIgnoreMatches,
  checkIgnorePatterns,
  type ResolvedIgnore,
  readIgnoreReport,
} from "./ignore-assertion.js";

// What the backend's `container` factory returns: anything with
// a getWorkspaceContainer() method — the shape withWorkspaceContainer
// installs. Same-DO callers pass `this`; cross-DO callers pass a
// DO stub whose target was extended with withWorkspaceContainer
// (Workers RPC exposes the method as a pipelined callable).
export interface ContainerHostHolder {
  getWorkspaceContainer(): IWorkspaceContainerAPI | Promise<IWorkspaceContainerAPI>;
}

export interface ContainerBackendOptions {
  // Resolves the container host to drive on each connect(). Called
  // anew per dial so a pool-backed factory can re-pick. Returning
  // a Promise is supported for pickers that consult external state
  // (KV, a coordinator DO, etc.).
  //
  // The returned value exposes getWorkspaceContainer() — the same
  // shape withWorkspaceContainer installs. Pass `this` (same-DO)
  // or a DO stub (cross-DO); the backend calls the method itself.
  container: () => ContainerHostHolder | Promise<ContainerHostHolder>;

  // Identifies the Workspace-owning DO. Fixed for the lifetime of
  // the backend: the backend lives inside this DO and the /api
  // upgrade always lands here. Plain {binding, id} data so it
  // survives the Workers RPC hop to a cross-DO container host.
  workspace: WorkspaceRef;

  // Hostname computerd will dial back. Defaults to "computer.internal".
  // Override for tests or to avoid collisions with other backends
  // sharing the same container host.
  egressHost?: string;

  egress?: WorkspaceEgressPolicy;

  // TCP port computerd listens on inside the container. Default 8080,
  // matching the Dockerfile shipped with examples/container.
  containerPort?: number;

  // Environment variables passed to container.start(). Merged onto
  // the defaults (PORT, MOUNT_POINT). Caller-supplied values win.
  containerEnv?: Record<string, string>;

  // Total time the backend waits for: container port to open,
  // /connect POST to return, /api upgrade to arrive. Default 30s.
  connectTimeoutMs?: number;

  // Period for the application-level heartbeat — a watermarks()
  // RPC on a timer. Two jobs: detect a silently-dead peer faster
  // than waiting for the next real RPC, and keep middlebox idle
  // timers warm. Default 20_000ms. Set 0 to disable.
  heartbeatIntervalMs?: number;

  // Paths the container keeps on its local disk instead of the
  // workspace (#179), as glob patterns passed to the container at start
  // time as MOUNT_IGNORE. Each starts with "/" (from the mount root) or
  // "**/" (at any depth); "*" matches within a path segment, and a
  // leading "!" excludes. The last matching pattern wins.
  //
  //   ignore: ["**/node_modules", "!/vendor/node_modules", "/dist"]
  //
  // The constructor throws on a pattern computerd would refuse. connect()
  // reads the applied patterns back off /__computerd/info and refuses the
  // connection if they disagree, which catches an image whose computerd
  // is too old to read them.
  ignore?: readonly string[];

  // Number of forced restart attempts after startup readiness
  // fails. The first attempt runs host.start() then probes computerd;
  // each restart attempt runs host.restart() then probes computerd
  // again. Defaults to 1 (one restart after the initial start).
  // Set 0 to disable restart on failed readiness.
  restartAttempts?: number;

  // Per-probe timeout for the startup health probe. Defaults to
  // 2 seconds. The shared probeComputerdHealth helper aborts the request
  // when it elapses; the next probe in the loop carries the
  // remaining readiness budget.
  healthProbeTimeoutMs?: number;

  // First retry delay after a failed startup probe. Defaults to
  // 250ms. Subsequent failures double the prior delay, capped at
  // healthRetryMaxDelayMs.
  healthRetryInitialDelayMs?: number;

  // Maximum delay between failed startup probes. Defaults to 2s.
  healthRetryMaxDelayMs?: number;

  // Selector this backend is registered under in Workspace.
  // Defaults to "container-shell"; override when the
  // workspace hosts more than one instance of the same backend
  // kind (e.g. two containers pinned to different pool members).
  id?: string;

  // Which prepared image to boot, as a key into
  // `ctx.container.images` — the names declared in the `images` block
  // of the wrangler containers config. Not the container application
  // name, which is a different field in that same block. Defaults to
  // "app", matching wrangler's convention.
  name?: string;

  // Instance size requested at launch. Under this scheduling policy
  // the wrangler containers block rejects `instance_type`, because
  // sizing belongs to the object once it owns the lifecycle, so this
  // is the only place to ask for one. Omitted lets the platform
  // choose, which is rarely what a workload that builds or compiles
  // wants.
  instance?: ContainerInstanceSize;

  // Remaining startup options, forwarded verbatim to
  // `ctx.container.start()`. `entrypoint`, `labels`, `hardTimeout`,
  // and the snapshot restore fields all live here. The fields this
  // backend owns are excluded: `env` is assembled from containerEnv,
  // `enableInternet` follows the egress policy, and `name` and
  // `instance` are named options above.
  launch?: Omit<ContainerLaunchSpec, "env" | "enableInternet" | "name" | "instance">;
}

const DEFAULT_EGRESS_HOST = "computer.internal";
// What the model is told about network access, by egress mode. The
// container only gets the internet with "direct"; "http-gateway"
// routes HTTP through the host, and "none" blocks it.
const NETWORK_DESCRIPTION: Record<WorkspaceEgressPolicy["mode"], string> = {
  direct: "It has network access.",
  "http-gateway": "Outbound HTTP goes through a gateway the host controls.",
  none: "It has no network access.",
};
// Image key assumed when a caller names none. Kept in step with the
// same default in container-host.ts, which resolves it.
const DEFAULT_IMAGE_NAME = "app";
// Paths the egress proxy serves. The container assembles no paths of
// its own, so these travel in the /connect request and both ends stay
// in step from one place.
const EGRESS_HEALTH_PATH = "/health";
const EGRESS_API_PATH = "/api";
const DEFAULT_CONTAINER_PORT = 8080;
const DEFAULT_CONNECT_TIMEOUT_MS = 30_000;
const DEFAULT_HEARTBEAT_INTERVAL_MS = 20_000;
const DEFAULT_RESTART_ATTEMPTS = 1;
const DEFAULT_HEALTH_PROBE_TIMEOUT_MS = 2_000;
const DEFAULT_HEALTH_RETRY_INITIAL_DELAY_MS = 250;
const DEFAULT_HEALTH_RETRY_MAX_DELAY_MS = 2_000;

// Bearer check for the dial-back. The scheme token is case-insensitive
// and may be followed by more than one space, matching what the daemon
// accepts on its own surface.
//
// The comparison walks every byte rather than stopping at the first
// difference, so it does not leak how much of the secret was correct.
// crypto.subtle.timingSafeEqual would be the primitive to reach for, but
// the SubtleCrypto this package compiles against does not declare it.
//
// An absent expected secret refuses everything rather than allowing it.
// This only runs once connect() has recorded the secret it launched the
// container with, so an absent one means the upgrade arrived without that
// having happened.
function bearerMatches(header: string | null, expected: string | undefined): boolean {
  if (expected === undefined || header === null) return false;
  const separator = header.indexOf(" ");
  if (separator === -1) return false;
  if (header.slice(0, separator).toLowerCase() !== "bearer") return false;
  const presented = header.slice(separator + 1).trim();
  if (presented.length !== expected.length) return false;
  let differences = 0;
  for (let i = 0; i < expected.length; i += 1) {
    differences |= presented.charCodeAt(i) ^ expected.charCodeAt(i);
  }
  return differences === 0;
}

/**
 * Runs shell commands in a Cloudflare Container through `computerd`.
 *
 * Experimental: this API may change in any release and is not yet
 * suitable for production use.
 */
export class ContainerBackend implements WorkspaceBackend {
  readonly type = "cloudflare-container";
  /** What this backend tells a model: a full Linux shell, its network access, and its slow start. */
  readonly description: string;
  readonly id: string;

  // `ignore` sits with the un-defaulted options rather than under
  // Required: undefined is a meaningful value for it (skip the check),
  // not a gap to be filled with a default.
  readonly #options: Required<
    Omit<
      ContainerBackendOptions,
      | "container"
      | "workspace"
      | "containerEnv"
      | "egress"
      | "id"
      | "name"
      | "instance"
      | "launch"
      | "ignore"
    >
  > &
    Pick<
      ContainerBackendOptions,
      "container" | "workspace" | "containerEnv" | "name" | "instance" | "launch" | "ignore"
    >;
  readonly #egress: WorkspaceEgressPolicy;
  readonly #egressToken: string | undefined;
  // Set once start() reports it, before the upgrade slot is armed, so
  // handleFetch can check the dial-back against it.
  #clientSecret: string | undefined;

  // State for the in-flight /api upgrade. handleFetch() resolves
  // #pendingUpgrade; connect() awaits it.
  #pendingUpgrade: Promise<WebSocket> | undefined;
  #resolveUpgrade: ((ws: WebSocket) => void) | undefined;
  #rejectUpgrade: ((err: unknown) => void) | undefined;

  // Cached after the first successful connect(). Cleared on close()
  // or when the underlying WebSocket reports `close` / `error`.
  #handle: BackendHandle | undefined;

  constructor(options: ContainerBackendOptions) {
    this.id = options.id ?? "container-shell";
    this.#egress = options.egress ?? { mode: "none" };
    this.#egressToken = this.#egress.mode === "http-gateway" ? crypto.randomUUID() : undefined;
    if (options.ignore !== undefined) checkIgnorePatterns(options.ignore);
    this.description = [
      "A shell in a full Linux container: npm, node, python, package managers, test runners, and native binaries.",
      NETWORK_DESCRIPTION[this.#egress.mode],
      "Starts much more slowly than an in-Worker backend because the container must boot.",
    ].join(" ");
    this.#options = {
      container: options.container,
      workspace: options.workspace,
      containerEnv: options.containerEnv,
      ignore: options.ignore,
      egressHost: options.egressHost ?? DEFAULT_EGRESS_HOST,
      containerPort: options.containerPort ?? DEFAULT_CONTAINER_PORT,
      connectTimeoutMs: options.connectTimeoutMs ?? DEFAULT_CONNECT_TIMEOUT_MS,
      heartbeatIntervalMs: options.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS,
      restartAttempts: options.restartAttempts ?? DEFAULT_RESTART_ATTEMPTS,
      healthProbeTimeoutMs: options.healthProbeTimeoutMs ?? DEFAULT_HEALTH_PROBE_TIMEOUT_MS,
      healthRetryInitialDelayMs:
        options.healthRetryInitialDelayMs ?? DEFAULT_HEALTH_RETRY_INITIAL_DELAY_MS,
      healthRetryMaxDelayMs: options.healthRetryMaxDelayMs ?? DEFAULT_HEALTH_RETRY_MAX_DELAY_MS,
      name: options.name,
      instance: options.instance,
      launch: options.launch,
    };
  }

  async connect(): Promise<BackendHandle> {
    if (this.#handle) return this.#handle;

    const deadline = Date.now() + this.#options.connectTimeoutMs;
    const holder = await this.#options.container();
    const host = await holder.getWorkspaceContainer();

    // Pre-flight: surface any prior container exit so a readiness
    // failure can attribute it back to the crash. host.start()
    // clears this once the new generation is up, so a successful
    // dial against a previously-dead container loses the
    // attribution — which is the right semantics; the prior exit
    // is only interesting if the new attempt fails too.
    const priorExit = await host.exitInfo().catch(() => null);

    const env = {
      PORT: String(this.#options.containerPort),
      MOUNT_POINT: WORKSPACE_ROOT,
      ...(this.#options.ignore !== undefined
        ? { MOUNT_IGNORE: this.#options.ignore.join(",") }
        : {}),
      ...this.#options.containerEnv,
    };
    let runtimeId: string;
    // The container requires this on its HTTP surface. It is durable, so
    // the value is the same across incarnations and across a replaced
    // container.
    let clientSecret: string;
    try {
      ({ runtimeId, clientSecret } = await host.start(this.#launchSpec(env)));
    } catch (error) {
      throw new WorkspaceTransportError(
        this.#formatStageError("start", {
          attempt: 1,
          maxAttempts: this.#options.restartAttempts + 1,
          restarts: 0,
          lastError: error,
          priorExit,
        }),
        { cause: error },
      );
    }
    try {
      await host.interceptOutboundHttp(this.#options.egressHost, this.#options.workspace);
      if (this.#egress.mode === "http-gateway" && this.#egressToken !== undefined) {
        await host.interceptAllOutboundHttp(this.#options.workspace, this.#egressToken);
      }
    } catch (error) {
      throw new WorkspaceTransportError(
        this.#formatStageError("egress", {
          attempt: 1,
          maxAttempts: this.#options.restartAttempts + 1,
          restarts: 0,
          lastError: error,
          priorExit,
        }),
        { cause: error },
      );
    }

    this.#clientSecret = clientSecret;

    // Arm the upgrade promise before posting /connect — computerd
    // dials back as soon as /health on the egress answers, so
    // the upgrade can arrive before the POST resolves.
    this.#armUpgrade();

    runtimeId = await this.#readyWithRestarts(host, env, deadline, priorExit, runtimeId);
    await this.#requireAuthEnforced(host, deadline);
    await this.#postConnect(host, deadline, clientSecret);
    const ws = await this.#waitForUpgrade(deadline);

    const stub = newWebSocketRpcSession(
      ws as unknown as globalThis.WebSocket,
    ) as RpcStub<WorkspaceRPC>;

    // `closed` resolves on the first 'close' event from the underlying
    // WebSocket. The Workspace listens for it and drops its cached
    // handle so the next ready() call rebuilds against a fresh
    // session.
    let stopHeartbeat: (() => void) | undefined;
    const closed = new Promise<void>((resolve) => {
      let fired = false;
      const onClose = () => {
        if (fired) return;
        fired = true;
        stopHeartbeat?.();
        resolve();
        this.#handle = undefined;
      };
      ws.addEventListener("close", onClose, { once: true });
      // Some runtimes fire 'error' without a follow-up 'close' on
      // abrupt teardown; treat error as close too.
      ws.addEventListener("error", onClose, { once: true });
      // capnweb's RPC layer can notice the session is broken (an
      // abort frame, a malformed message) before the underlying
      // WebSocket fires close. onRpcBroken closes that gap so the
      // next ready() rebuilds against a fresh transport instead of
      // waiting on a heartbeat or the next real RPC to discover
      // the wedged session.
      (stub as unknown as { onRpcBroken: (cb: (err: unknown) => void) => void }).onRpcBroken(
        onClose,
      );
    });

    if (this.#options.heartbeatIntervalMs > 0) {
      stopHeartbeat = startHeartbeat({
        intervalMs: this.#options.heartbeatIntervalMs,
        ping: () => (stub as unknown as WorkspaceRPC).sync.watermarks(),
        onFailure: () => {
          try {
            ws.close();
          } catch {
            // already closed; idempotent
          }
        },
      });
    }

    // Checked before the handle is published, so a mismatched image
    // never serves a single command. Doing this after connect() returned
    // would let the first exec write into a path the caller believes is
    // local-only, which is precisely the state that is expensive to
    // discover later.
    const resolvedIgnore = await this.#resolveIgnore(host, clientSecret);
    try {
      assertIgnoreMatches(this.#options.ignore, resolvedIgnore);
    } catch (error) {
      // Tear the transport down rather than leaking a live socket for a
      // connection the caller is not going to get.
      try {
        (stub as unknown as Disposable)[Symbol.dispose]?.();
      } catch {
        // already disposed; idempotent
      }
      try {
        ws.close();
      } catch {
        // already closed; idempotent
      }
      stopHeartbeat?.();
      throw error;
    }

    const handle: BackendHandle = {
      rpc: stub as unknown as WorkspaceRPC,
      runtimeId,
      closed,
      ignore: resolvedIgnore,
      close: async () => {
        stopHeartbeat?.();
        // Dispose the root stub first. Per capnweb's docs, this is
        // the documented way to shut a session down — it lets the
        // RPC layer send a clean abort frame to the peer before
        // the socket dies. Falling through to ws.close() is
        // belt-and-braces for runtimes where the dispose path
        // doesn't (yet) close the transport.
        try {
          (stub as unknown as Disposable)[Symbol.dispose]?.();
        } catch {
          // already disposed; idempotent
        }
        try {
          ws.close();
        } catch {
          // already closed; idempotent
        }
        this.#handle = undefined;
      },
    };
    this.#handle = handle;
    return handle;
  }

  // Routes an /api upgrade Request into the in-flight connect().
  // Returns the 101 response that the WorkspaceProxy fetch handler
  // forwards back to the container.
  async handleFetch(req: Request): Promise<Response> {
    if (
      this.#egress.mode === "http-gateway" &&
      this.#egressToken !== undefined &&
      req.headers.get(WORKSPACE_EGRESS_TOKEN_HEADER) === this.#egressToken
    ) {
      const headers = new Headers(req.headers);
      const originalUrl = headers.get(WORKSPACE_EGRESS_URL_HEADER);
      let parsedUrl: URL;
      try {
        parsedUrl = new URL(originalUrl ?? "");
      } catch {
        return new Response("invalid egress URL", { status: 400 });
      }
      if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
        return new Response("invalid egress URL", { status: 400 });
      }
      headers.delete(WORKSPACE_EGRESS_TOKEN_HEADER);
      headers.delete(WORKSPACE_EGRESS_URL_HEADER);
      const sanitized = new Request(req, { headers });
      return this.#egress.gateway.fetch(new Request(parsedUrl, sanitized));
    }
    const url = new URL(req.url);
    if (url.pathname !== EGRESS_API_PATH) {
      return new Response("not found", { status: 404 });
    }
    // A request with no Upgrade header is a malformed handshake, which
    // is a 400. 426 belongs to the narrower case of a version this end
    // does not speak, and is what the daemon answers for it.
    if (req.headers.get("upgrade") !== "websocket") {
      return new Response(`${EGRESS_API_PATH} requires a websocket upgrade`, { status: 400 });
    }
    // The slot armed by connect() hands its session to whoever arrives
    // first, and this endpoint is reachable from inside the container
    // through the egress interceptor. Without a token, any command the
    // workspace runs could take the daemon's place and become the peer
    // this durable object pushes its files to and takes its exec output
    // from. The daemon presents the secret it was launched with.
    if (!bearerMatches(req.headers.get("authorization"), this.#clientSecret)) {
      return new Response("unauthorized", {
        status: 401,
        headers: { "www-authenticate": "Bearer" },
      });
    }

    const pair = new WebSocketPair();
    const [client, server] = [pair[0], pair[1]];
    server.accept();

    if (this.#resolveUpgrade) {
      this.#resolveUpgrade(server);
    } else {
      // No connect() in flight — close the socket immediately.
      // The remote will redial on its next attempt; we don't
      // hold orphaned sockets that nothing will reap.
      server.close(1011, "no pending connect");
      return new Response("no pending connect", { status: 409 });
    }

    return new Response(null, { status: 101, webSocket: client });
  }

  // --- internals --------------------------------------------------

  /**
   * Assemble the launch spec both launch paths use.
   *
   * connect() and #readyWithRestarts each reach the platform through a
   * different host method, and a spec built separately in each place
   * drifts: the restart silently drops whatever the initial start was
   * given, and the replacement container comes up with a different
   * image or size than the one it replaced. One construction site is
   * what keeps them honest.
   */
  #launchSpec(env: Record<string, string>): ContainerLaunchSpec {
    return {
      ...this.#options.launch,
      env,
      enableInternet: this.#egress.mode === "direct",
      name: this.#options.name ?? DEFAULT_IMAGE_NAME,
      ...(this.#options.instance === undefined ? {} : { instance: this.#options.instance }),
    };
  }

  #armUpgrade(): void {
    this.#pendingUpgrade = new Promise<WebSocket>((resolve, reject) => {
      this.#resolveUpgrade = resolve;
      this.#rejectUpgrade = reject;
    });
    // Swallow unhandled-rejection noise if connect() throws
    // before anyone awaits the promise.
    this.#pendingUpgrade.catch(() => {});
  }

  #clearUpgrade(): void {
    this.#pendingUpgrade = undefined;
    this.#resolveUpgrade = undefined;
    this.#rejectUpgrade = undefined;
  }

  // Drive startup readiness with bounded restart attempts. Each
  // attempt runs the shared probe in a backoff loop until either
  // computerd answers, the per-attempt budget elapses, or the overall
  // connect deadline elapses. On a failed attempt with restarts
  // remaining, run host.restart(env) and try again.
  async #readyWithRestarts(
    host: IWorkspaceContainerAPI,
    env: Record<string, string>,
    deadline: number,
    priorExit: { exitedAt: number; reason: string } | null,
    initialRuntimeId: string,
  ): Promise<string> {
    const maxAttempts = this.#options.restartAttempts + 1;
    // Split the remaining time across attempts so a failing
    // first attempt doesn't starve the restart-retry. Floor at
    // 250ms so a near-deadline last attempt still has room to
    // dispatch a probe rather than collapsing to ~1ms.
    const totalBudget = Math.max(0, deadline - Date.now());
    const perAttemptBudget = Math.max(250, Math.floor(totalBudget / maxAttempts));
    let attempt = 0;
    let restarts = 0;
    let lastError: unknown;
    let runtimeId = initialRuntimeId;

    while (attempt < maxAttempts) {
      attempt++;
      const attemptDeadline = Math.min(deadline, Date.now() + perAttemptBudget);
      const ok = await this.#probeUntilHealthy(host, attemptDeadline).then(
        () => true,
        (error) => {
          lastError = error;
          return false;
        },
      );
      if (ok) return runtimeId;

      if (attempt < maxAttempts) {
        try {
          ({ runtimeId } = await host.restart(this.#launchSpec(env)));
          restarts++;
        } catch (error) {
          this.#rejectUpgrade?.(error);
          this.#clearUpgrade();
          throw new WorkspaceTransportError(
            this.#formatStageError("restart", {
              attempt,
              maxAttempts,
              restarts,
              lastError: error,
            }),
            { cause: error },
          );
        }
      }
    }

    this.#rejectUpgrade?.(new Error("computerd never became healthy"));
    this.#clearUpgrade();
    throw new WorkspaceTransportError(
      this.#formatStageError("health", {
        attempt,
        maxAttempts,
        restarts,
        lastError,
        priorExit,
      }),
      lastError instanceof Error ? { cause: lastError } : undefined,
    );
  }

  // Reads the container's local-only path configuration.
  //
  // A failure to reach /__computerd/info is reported as "unsupported"
  // rather than propagated. The endpoint is diagnostic, and a client
  // that declared no `ignore` should not lose a working connection
  // because a diagnostic request failed. A client that *did* declare
  // one still fails, via assertIgnoreMatches -- which is the right
  // split: silence is only acceptable when nobody asked.
  //
  // The endpoint sits behind the client secret like every route except
  // /health. Without the token an enforcing container answers 401, which
  // would read as "unsupported" and fail every connect that declared
  // `ignore`.
  async #resolveIgnore(
    host: IWorkspaceContainerAPI,
    clientSecret: string,
  ): Promise<ResolvedIgnore> {
    try {
      const res = await host.fetchPort(
        this.#options.containerPort,
        "http://container/__computerd/info",
        {
          headers: { authorization: `Bearer ${clientSecret}` },
          signal: AbortSignal.timeout(this.#options.healthProbeTimeoutMs),
        },
      );
      if (!res.ok)
        return { patterns: [], root: undefined, mountPoint: undefined, supported: false };
      return readIgnoreReport(await res.json());
    } catch {
      return { patterns: [], root: undefined, mountPoint: undefined, supported: false };
    }
  }

  async #probeUntilHealthy(host: IWorkspaceContainerAPI, deadline: number): Promise<void> {
    let delay = this.#options.healthRetryInitialDelayMs;
    let lastError: unknown;
    while (Date.now() < deadline) {
      try {
        await probeComputerdHealth(host, {
          port: this.#options.containerPort,
          path: "/health",
          timeoutMs: Math.min(
            this.#options.healthProbeTimeoutMs,
            Math.max(50, deadline - Date.now()),
          ),
        });
        return;
      } catch (error) {
        lastError = error;
        const remaining = deadline - Date.now();
        if (remaining <= 0) break;
        await sleep(Math.min(delay, remaining));
        delay = Math.min(delay * 2, this.#options.healthRetryMaxDelayMs);
      }
    }
    // oxlint-disable-next-line typescript/only-throw-error -- rethrows a caught value
    throw lastError ?? new Error("computerd health probe timed out");
  }

  #formatStageError(
    stage: "start" | "egress" | "health" | "restart" | "connect" | "ws",
    info: {
      attempt: number;
      maxAttempts: number;
      restarts: number;
      lastError: unknown;
      priorExit?: { exitedAt: number; reason: string } | null;
    },
  ): string {
    const priorExit = info.priorExit ? ` priorExit=${JSON.stringify(info.priorExit.reason)}` : "";
    return (
      `ContainerBackend(${this.id}): connect failed at ` +
      `stage=${stage} port=${this.#options.containerPort} ` +
      `attempt=${info.attempt}/${info.maxAttempts} restarts=${info.restarts} ` +
      `timeoutMs=${this.#options.connectTimeoutMs}${priorExit} ` +
      `lastError=${describeError(info.lastError)}`
    );
  }

  // Confirm the container refuses an unauthorized request before handing
  // it a session. Launching with the secret is arranged elsewhere, but an
  // image built before the daemon understood RPC_CLIENT_SECRET ignores it
  // and serves everything, and that is invisible from the host: the
  // bearer token goes out and the container is content either way.
  //
  // /api is the probe target because it exists on every daemon that has a
  // capnweb endpoint at all, so this check does not depend on which
  // diagnostic routes a given build happens to expose. An enforcing
  // daemon answers 401 before it looks at the route; one that is not
  // enforcing answers whatever the route says for an unauthenticated
  // GET.
  //
  // A definite answer other than 401 fails the connect. Recycling a
  // container that predates the secret is the cost of the upgrade, and it
  // is preferable to a workspace that believes it is authorized and is
  // not. A probe that cannot complete is not evidence either way and is
  // allowed through: the readiness loop above is what decides whether the
  // container is alive.
  async #requireAuthEnforced(host: IWorkspaceContainerAPI, deadline: number): Promise<void> {
    let status: number;
    try {
      const res = await host.fetchPort(this.#options.containerPort, "http://container/api", {
        // Bounded like every other request this file makes to the
        // container. Unbounded, a container that accepts the connection
        // and then stops serving would hang the connect, and a slow one
        // would eat the budget #waitForUpgrade needs, surfacing as an
        // upgrade that never arrived.
        signal: AbortSignal.timeout(
          Math.min(this.#options.healthProbeTimeoutMs, Math.max(50, deadline - Date.now())),
        ),
      });
      status = res.status;
      // Release the body; nothing here reads it.
      await res.text().catch(() => "");
    } catch {
      return;
    }
    if (status === 401) return;
    this.#rejectUpgrade?.(new Error("container is not enforcing RPC_CLIENT_SECRET"));
    this.#clearUpgrade();
    throw new WorkspaceTransportError(
      `ContainerBackend(${this.id}) [stage=auth]: container served an unauthenticated ` +
        `request to /api with ${status}, so this workspace would run without authorization. ` +
        `A container or image predating RPC_CLIENT_SECRET has to be recycled.`,
    );
  }

  async #postConnect(
    host: IWorkspaceContainerAPI,
    deadline: number,
    clientSecret: string,
  ): Promise<void> {
    const remaining = Math.max(0, deadline - Date.now());
    let res: Response;
    try {
      res = await host.fetchPort(this.#options.containerPort, "http://container/connect", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${clientSecret}`,
        },
        body: JSON.stringify({
          base: `http://${this.#options.egressHost}`,
          health: EGRESS_HEALTH_PATH,
          api: EGRESS_API_PATH,
          healthTimeoutMs: remaining,
        }),
      });
    } catch (error) {
      this.#rejectUpgrade?.(error);
      this.#clearUpgrade();
      throw new WorkspaceTransportError(
        `ContainerBackend(${this.id}) [stage=connect]: POST /connect failed: ${describeError(error)}`,
        { cause: error },
      );
    }
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      const cause = new Error(`/connect ${res.status}`);
      this.#rejectUpgrade?.(cause);
      this.#clearUpgrade();
      throw new WorkspaceTransportError(
        `ContainerBackend(${this.id}) [stage=connect]: POST /connect returned ${res.status}: ${body}`,
        { cause },
      );
    }
  }

  async #waitForUpgrade(deadline: number): Promise<WebSocket> {
    const upgrade = this.#pendingUpgrade;
    if (!upgrade) throw new Error("ContainerBackend: upgrade promise missing");

    const remaining = Math.max(0, deadline - Date.now());
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const ws = await Promise.race([
        upgrade,
        new Promise<never>((_, reject) => {
          timer = setTimeout(
            () =>
              reject(
                new WorkspaceTransportError(
                  `ContainerBackend(${this.id}) [stage=ws]: /api upgrade did not arrive within ${this.#options.connectTimeoutMs}ms`,
                ),
              ),
            remaining,
          );
        }),
      ]);
      return ws;
    } finally {
      if (timer) clearTimeout(timer);
      this.#clearUpgrade();
    }
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function describeError(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}
